import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { FaEye, FaPlay, FaChevronDown, FaChevronUp, FaUser, FaMapMarkerAlt, FaTag, FaCalendarAlt, FaUsers, FaVideo, FaClock, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const RunningVoice = () => {
  const [runningVoices, setRunningVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectLoading, setRejectLoading] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [showScreenshotModal, setShowScreenshotModal] = useState(false);
  const [currentScreenshot, setCurrentScreenshot] = useState(null);
  // Spinner for Accept/Reject actions
  const [showAcceptSpinner, setShowAcceptSpinner] = useState(false);
  const [showRejectSpinner, setShowRejectSpinner] = useState(false);
  // Remove per-adId group pagination
  const rowsPerPage = 10;
  // Global ads pagination
  const [adsPage, setAdsPage] = useState(1);
  const adsPerPage = 10;
  // State for collapsible reporters list
  const [expandedCards, setExpandedCards] = useState({});

  // Toggle function for expanding/collapsing reporters list
  const toggleCardExpansion = (adId) => {
    setExpandedCards(prev => ({
      ...prev,
      [adId]: !prev[adId]
    }));
  };

  // Accept voice proof
  const handleAcceptSubmit = async (adId, reporterId) => {
    console.log("=== ACCEPT VOICE PROOF DEBUG ===");
    console.log("Received adId:", adId, "Type:", typeof adId);
    console.log("Received reporterId:", reporterId, "Type:", typeof reporterId);

    // Ensure we have string IDs
    const adIdStr = typeof adId === 'object' ? adId?._id || adId : adId;
    const reporterIdStr = typeof reporterId === 'object' ? reporterId?._id || reporterId : reporterId;

    console.log("Processed adId:", adIdStr, "Type:", typeof adIdStr);
    console.log("Processed reporterId:", reporterIdStr, "Type:", typeof reporterIdStr);

    if (!adIdStr || !reporterIdStr) {
      console.error("Missing required IDs:", { adIdStr, reporterIdStr });
      toast.error("Missing required IDs for acceptance");
      return;
    }

    setShowAcceptSpinner(true);
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/admin/accept/proof/${adIdStr}/${reporterIdStr}`;
      console.log("Making request to:", url);

      const response = await axios.put(
        url,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log('Response accept voice proof:', response);
      toast.success("Voice proof accepted successfully!");
      setTimeout(() => {
        setShowAcceptSpinner(false);
        window.location.reload();
      }, 1200);
    } catch (err) {
      setShowAcceptSpinner(false);
      console.error("Accept voice proof error:", err);
      console.error("Error response:", err.response?.data);
      toast.error("Failed to accept voice proof. Try again.");
    }
  };

  // Reject voice proof
  const handleRejectSubmit = async (proofId) => {
    console.log('Rejecting voice proofid:', proofId);
    setRejectLoading(true);
    setShowRejectSpinner(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/reject/proof/${proofId}`,
        { rejectionNote: rejectReason },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log('Response reject voice proof:', response);
      toast.success("Voice proof rejected successfully!");
      setShowRejectModal(false);
      setRejectReason("");
      setRejectTarget(null);
      setTimeout(() => {
        setShowRejectSpinner(false);
        window.location.reload();
      }, 1200);
    } catch (err) {
      setShowRejectSpinner(false);
      toast.error("Failed to reject voice. Try again.");
    } finally {
      setRejectLoading(false);
    }
  };

  useEffect(() => {
    const fetchRunningVoices = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/admin/get/running/ryv/ads`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log('Response get new running ads:', response);

        // Handle response - backend now returns success: true with empty array when no data
        if (response.data.success === true) {
          setRunningVoices(
            Array.isArray(response.data.data) ? response.data.data : []
          );
          if (response.data.data && response.data.data.length === 0) {
            console.log('No running voices found:', response.data.message);
          }
        } else {
          // Handle any unexpected error responses
          console.error('Unexpected response format:', response.data);
          setRunningVoices([]);
        }
      } catch (err) {
        setError("Failed to fetch running voices");
      } finally {
        setLoading(false);
      }
    };
    fetchRunningVoices();
  }, []);


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }
  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  // Group voices by adId
  // Process the grouped data from backend
  const grouped = runningVoices.map(voiceGroup => {
    const adId = voiceGroup.adId._id.toString();
    // Combine accepted users and uploaded users
    const allUsers = [
      ...(voiceGroup.acceptedUsers || []).map(user => ({ ...user, type: 'accepted' })),
      ...(voiceGroup.uploadedUsers || []).map(user => ({ ...user, type: 'uploaded' }))
    ];
    // Remove duplicates based on reporter ID
    const uniqueUsers = allUsers.filter((user, index, self) =>
      index === self.findIndex(u => u._id === user._id)
    );
    return [adId, uniqueUsers];
  }).sort((a, b) => {
    // Sort groups by the newest acceptedAt/uploadedAt in each group
    const getLatest = (users) => Math.max(...users.map(u => {
      const date = u.acceptedAt || u.uploadedAt;
      return new Date(date).getTime();
    }));
    return getLatest(b[1]) - getLatest(a[1]);
  });

  return (
    <div className="p-6">
      {/* Spinner Overlay for Reject */}
      {showRejectSpinner && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="flex flex-col items-center p-8 bg-white rounded-2xl shadow-2xl border border-blue-200 animate-fadeIn">
            {/* Custom Tailwind Spinner */}
            <div className="relative mb-4 w-16 h-16 grid place-items-center">
              {/* Outer ring */}
              <span
                className="absolute inset-0 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin"
                style={{ borderWidth: "4px" }}
              />
              {/* Middle ring */}
              <span
                className="absolute inset-0 m-1 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin-slow"
                style={{ borderWidth: "4px", animationDuration: "2s" }}
              />
              {/* Inner ring */}
              <span
                className="absolute inset-0 m-2 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin-slower"
                style={{ borderWidth: "4px", animationDuration: "3s" }}
              />
            </div>
            <span className="text-lg font-semibold text-blue-700 tracking-wide">
              Processing Rejection...
            </span>
            <span className="text-sm text-gray-500 mt-2">
              Please wait while we update the status.
            </span>
          </div>
        </div>
      )}
      {/* Spinner Overlay for Accept */}
      {showAcceptSpinner && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="flex flex-col items-center p-8 bg-white rounded-2xl shadow-2xl border border-blue-200 animate-fadeIn">
            {/* Custom Tailwind Spinner */}
            <div className="relative mb-4 w-16 h-16 grid place-items-center">
              {/* Outer ring */}
              <span
                className="absolute inset-0 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin"
                style={{ borderWidth: "4px" }}
              />
              {/* Middle ring */}
              <span
                className="absolute inset-0 m-1 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin-slow"
                style={{ borderWidth: "4px", animationDuration: "2s" }}
              />
              {/* Inner ring */}
              <span
                className="absolute inset-0 m-2 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin-slower"
                style={{ borderWidth: "4px", animationDuration: "3s" }}
              />
            </div>
            <span className="text-lg font-semibold text-blue-700 tracking-wide">
              Processing Acceptance...
            </span>
            <span className="text-sm text-gray-500 mt-2">
              Please wait while we update the status.
            </span>
          </div>
        </div>
      )}
      {/* Screenshot Modal (future use) */}
      {showScreenshotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm transition-all">
          <div className="relative bg-gradient-to-br from-white via-gray-50 to-gray-200 rounded-2xl shadow-2xl p-0 max-w-2xl w-full border border-gray-200 animate-fadeIn">
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-red-500 text-3xl font-bold focus:outline-none focus:ring-2 focus:ring-red-300 transition"
              onClick={() => setShowScreenshotModal(false)}
              aria-label="Close"
            >
              &times;
            </button>
            <div className="flex flex-col items-center justify-center p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-800 flex items-center gap-2">
                <FaEye className="text-blue-500" /> Screenshot Preview
              </h2>
              <div className="w-full flex items-center justify-center min-h-[300px] bg-gray-100 rounded-xl border border-dashed border-gray-300 shadow-inner overflow-hidden">
                <img
                  src={currentScreenshot}
                  alt="Screenshot"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/fallback-image.jpg";
                  }}
                  className="max-h-[60vh] max-w-full rounded-xl shadow-lg border border-gray-200 object-contain transition-transform duration-300 hover:scale-105"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">
          Running  Accepted by Reporter
        </h1>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/admin/runningvoice"
            className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
          >
            Accepted By Reporter
          </Link>
          <Link
            to="/admin/pendingvoice"
            className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600"
          >
            Pending
          </Link>
          <Link
            to="/admin/approvedvoice"
            className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
          >
            Approved
          </Link>
          <Link
            to="/admin/rejectedvoice"
            className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
          >
            Rejected
          </Link>
        </div>
      </div>

      {/* Stats Card */}
      <div className="mb-8">
        <div className="bg-teal-500 p-6 rounded-xl shadow-lg text-white">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm opacity-80">Running Accepted by Reporter</p>
              <p className="text-2xl font-bold">{runningVoices.length}</p>
            </div>
            <FaUsers className="w-10 h-10 opacity-80" />
          </div>
        </div>
      </div>

      {/* Grouped AdId Cards with global pagination */}
      {grouped.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <FaUsers className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            No Running Voices Found
          </h3>
          <p className="text-gray-500 mb-4">
            There are currently no Raise Your Voice posts with accepted reporters or uploaded proofs.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-700">
            <p className="font-medium mb-1">💡 What you'll see here:</p>
            <ul className="text-left space-y-1">
              <li>• RYV posts that reporters have accepted</li>
              <li>• RYV posts with uploaded proof videos</li>
              <li>• Posts ready for admin review and approval</li>
            </ul>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4">
            {grouped
              .slice((adsPage - 1) * adsPerPage, adsPage * adsPerPage)
              .map(([adId, voices]) => {
                const isExpanded = expandedCards[adId];
                // Find the voice group that contains this adId
                const voiceGroup = runningVoices.find(vg => vg.adId._id.toString() === adId);
                const adDetails = voiceGroup?.adId;

                return (
                  <div key={adId} className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                    {/* RYV Details Header */}
                    <div className="p-4 border-b border-gray-100">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-800 mb-2">
                            RYV ID: <span className="text-blue-600 font-mono">{adId}</span>
                          </h3>

                          {/* RYV Details */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                            <div className="flex flex-col gap-1 text-gray-600">
                              <div className="flex items-center gap-2">
                                <FaMapMarkerAlt className="w-4 h-4 text-green-500 flex-shrink-0" />
                                <span className="font-medium">Location:</span>
                                <span className="text-gray-800 truncate">{adDetails?.state || "N/A"}, {adDetails?.city || "N/A"}</span>
                              </div>
                              <div className="text-xs text-gray-500 ml-6">
                                {adDetails?.residenceAddress || "N/A"}
                              </div>
                            </div>

                            <div className="flex items-center gap-2 text-gray-600">
                              <FaTag className="w-4 h-4 text-purple-500 flex-shrink-0" />
                              <span className="font-medium">Target:</span>
                              <span className="text-gray-800 capitalize truncate">{adDetails?.targetUserType || "N/A"}</span>
                            </div>

                            <div className="flex items-center gap-2 text-gray-600">
                              <FaUsers className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                              <span className="font-medium">Reporters:</span>
                              <div className="flex gap-2">
                                <span className="text-gray-800 font-semibold bg-blue-100 px-2 py-1 rounded-full text-xs">
                                  Accepted: {voiceGroup?.totalAccepted || 0}
                                </span>
                                <span className="text-gray-800 font-semibold bg-green-100 px-2 py-1 rounded-full text-xs">
                                  Uploaded: {voiceGroup?.totalUploaded || 0}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Description */}
                          {adDetails?.description && (
                            <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                              <p className="text-sm text-gray-700">
                                <span className="font-medium">Description:</span> {adDetails.description}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Toggle Button */}
                        <button
                          onClick={() => toggleCardExpansion(adId)}
                          className="flex items-center gap-2 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium flex-shrink-0"
                        >
                          {isExpanded ? (
                            <>
                              <FaChevronUp className="w-4 h-4" />
                              <span className="hidden sm:inline">Hide Reporters</span>
                              <span className="sm:hidden">Hide</span>
                            </>
                          ) : (
                            <>
                              <FaChevronDown className="w-4 h-4" />
                              <span className="hidden sm:inline">Show Reporters ({voices.length})</span>
                              <span className="sm:hidden">Show ({voices.length})</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Collapsible Reporters List */}
                    {isExpanded && (
                      <div className="p-4">
                        <div className="mb-4">
                          <h4 className="text-md font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <FaUsers className="w-4 h-4 text-blue-500" />
                            Reporters ({voices.length})
                          </h4>
                        </div>

                        {/* Desktop Table View */}
                        <div className="hidden lg:block overflow-x-auto">
                          <div className="min-w-full">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reporter ID</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Video</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitted</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {voices.map((voice, idx) => (
                                  <tr key={voice._id || idx} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                      <div className="flex items-center gap-2">
                                        <FaUser className="w-3 h-3 text-gray-400" />
                                        {voice.iinsafId || "N/A"}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900">
                                      {voice.name || "N/A"}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900">
                                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${voice.type === 'uploaded'
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-blue-100 text-blue-800'
                                        }`}>
                                        {voice.type === 'uploaded' ? 'Proof Uploaded' : 'Accepted Only'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900">
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        {voice.platform || "N/A"}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900">
                                      {voice.videoLink ? (
                                        <a
                                          href={voice.videoLink}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center gap-1 px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm transition-colors"
                                        >
                                          <FaVideo className="w-3 h-3" /> Watch
                                        </a>
                                      ) : (
                                        <span className="text-gray-400">N/A</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900">
                                      <div className="flex items-center gap-1">
                                        <FaClock className="w-3 h-3 text-gray-400" />
                                        {voice.duration || "N/A"}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900">
                                      <div className="flex items-center gap-1">
                                        <FaCalendarAlt className="w-3 h-3 text-gray-400" />
                                        {(() => {
                                          const dateField = voice.acceptedAt || voice.uploadedAt;
                                          if (!dateField) return "N/A";
                                          const d = new Date(dateField);
                                          if (isNaN(d.getTime())) return "Invalid Date";
                                          const date = d.toLocaleDateString("en-GB");
                                          const time = d.toLocaleTimeString("en-GB", { hour12: true });
                                          return (
                                            <div className="text-xs">
                                              <div>{date}</div>
                                              <div className="text-gray-500">{time}</div>
                                            </div>
                                          );
                                        })()}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3">
                                      <div className="flex gap-2">
                                        {voice.type === 'uploaded' ? (
                                          <>
                                            <button
                                              className="flex items-center gap-1 px-3 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 text-sm transition-colors"
                                              onClick={() => handleAcceptSubmit(adDetails?._id, voice._id)}
                                            >
                                              <FaCheckCircle className="w-3 h-3" /> Accept
                                            </button>
                                            <button
                                              className="flex items-center gap-1 px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm transition-colors"
                                              onClick={() => {
                                                setRejectTarget({
                                                  proofId: voice.proofId,
                                                });
                                                setShowRejectModal(true);
                                              }}
                                            >
                                              <FaTimesCircle className="w-3 h-3" /> Reject
                                            </button>
                                          </>
                                        ) : (
                                          <span className="text-gray-400 text-sm">No proof uploaded</span>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* Mobile Card View */}
                        <div className="lg:hidden space-y-3">
                          {voices.map((voice, idx) => (
                            <div key={voice._id || idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                              <div className="space-y-3">
                                {/* Reporter Info */}
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <FaUser className="w-4 h-4 text-blue-500" />
                                    <span className="font-medium text-gray-900">{voice.iinsafId || "N/A"}</span>
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      {voice.platform || "N/A"}
                                    </span>
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${voice.type === 'uploaded'
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-blue-100 text-blue-800'
                                      }`}>
                                      {voice.type === 'uploaded' ? 'Proof Uploaded' : 'Accepted Only'}
                                    </span>
                                  </div>
                                </div>

                                {/* Name */}
                                <div className="text-sm text-gray-700">
                                  <span className="font-medium">Name:</span> {voice.name || "N/A"}
                                </div>

                                {/* Duration */}
                                <div className="flex items-center gap-2 text-sm text-gray-700">
                                  <FaClock className="w-3 h-3 text-gray-400" />
                                  <span className="font-medium">Duration:</span>
                                  <span>{voice.duration || "N/A"}</span>
                                </div>

                                {/* Submitted Date */}
                                <div className="flex items-center gap-2 text-sm text-gray-700">
                                  <FaCalendarAlt className="w-3 h-3 text-gray-400" />
                                  <span className="font-medium">Submitted:</span>
                                  <span>
                                    {(() => {
                                      const dateField = voice.acceptedAt || voice.uploadedAt;
                                      if (!dateField) return "N/A";
                                      const d = new Date(dateField);
                                      if (isNaN(d.getTime())) return "Invalid Date";
                                      const date = d.toLocaleDateString("en-GB");
                                      const time = d.toLocaleTimeString("en-GB", { hour12: true });
                                      return `${date} ${time}`;
                                    })()}
                                  </span>
                                </div>

                                {/* Video Link */}
                                {voice.videoLink && (
                                  <div>
                                    <a
                                      href={voice.videoLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-2 px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm transition-colors"
                                    >
                                      <FaVideo className="w-4 h-4" /> Watch Video
                                    </a>
                                  </div>
                                )}

                                {/* Actions */}
                                <div className="flex gap-2 pt-2 border-t border-gray-200">
                                  {voice.type === 'uploaded' ? (
                                    <>
                                      <button
                                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 text-sm transition-colors"
                                        onClick={() => handleAcceptSubmit(adDetails?._id, voice._id)}
                                      >
                                        <FaCheckCircle className="w-4 h-4" /> Accept
                                      </button>
                                      <button
                                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm transition-colors"
                                        onClick={() => {
                                          setRejectTarget({
                                            proofId: voice.proofId,
                                          });
                                          setShowRejectModal(true);
                                        }}
                                      >
                                        <FaTimesCircle className="w-4 h-4" /> Reject
                                      </button>
                                    </>
                                  ) : (
                                    <div className="flex-1 flex items-center justify-center px-3 py-2 text-gray-400 text-sm">
                                      No proof uploaded
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
          {/* Global Pagination Controls for ads */}
          {grouped.length > adsPerPage && (
            <div className="flex justify-center mt-8">
              <nav className="inline-flex flex-wrap items-center space-x-2">
                <button
                  onClick={() => setAdsPage(adsPage - 1)}
                  disabled={adsPage === 1}
                  className={`px-4 py-1 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors ${adsPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Previous
                </button>
                {[...Array(Math.ceil(grouped.length / adsPerPage))].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setAdsPage(i + 1)}
                    className={`px-3 py-1 rounded border border-yellow-400 text-yellow-900 font-bold bg-yellow-100 mx-1 ${adsPage === i + 1 ? 'bg-yellow-300' : ''}`}
                    aria-current={adsPage === i + 1 ? 'page' : undefined}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => setAdsPage(adsPage + 1)}
                  disabled={adsPage === Math.ceil(grouped.length / adsPerPage)}
                  className={`px-4 py-1 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors ${adsPage === Math.ceil(grouped.length / adsPerPage) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}
        </>
      )}

      {/* Reject Reason Modal */}
      {showRejectModal && rejectTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md border border-red-200 relative animate-fadeIn">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-red-500 text-2xl font-bold focus:outline-none"
              onClick={() => {
                setShowRejectModal(false);
                setRejectReason("");
                setRejectTarget(null);
              }}
              aria-label="Close"
            >
              &times;
            </button>
            <h2 className="text-xl font-bold mb-4 text-red-600 flex items-center gap-2">
              <FaTimesCircle className="w-5 h-5" />
              Reject Voice Proof
            </h2>
            <p className="mb-4 text-gray-700">
              Please provide a reason for rejection:
            </p>
            <textarea
              className="w-full border border-gray-300 rounded-lg p-3 mb-4 min-h-[100px] focus:ring-2 focus:ring-red-200 focus:border-red-300 resize-none"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason..."
              disabled={rejectLoading}
            />
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 text-gray-700 font-medium transition-colors"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason("");
                  setRejectTarget(null);
                }}
                disabled={rejectLoading}
              >
                Cancel
              </button>
              <button
                className={`px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 font-semibold transition-colors flex items-center gap-2 ${!rejectReason.trim() || rejectLoading
                  ? "opacity-50 cursor-not-allowed"
                  : ""
                  }`}
                onClick={() => handleRejectSubmit(rejectTarget.proofId)}
                disabled={!rejectReason.trim() || rejectLoading}
              >
                {rejectLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Rejecting...
                  </>
                ) : (
                  <>
                    <FaTimesCircle className="w-4 h-4" />
                    Submit
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RunningVoice;
